import { useState, useEffect, useMemo } from "react";
import layout from "../data/photos";
import "../styles/components/gallery.scss";

function GalleryGrid() {
  const items = layout.desktop;
  
  // Filter to only clickable items for navigation
  const clickableItems = useMemo(() => {
    return items.filter((it) => it.clickable !== false);
  }, [items]);
  
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [loadedImages, setLoadedImages] = useState(new Set());

  // Preload first few visible images on mount
  useEffect(() => {
    const priorityImages = items.slice(0, 5).filter((it) => it.clickable !== false);
    priorityImages.forEach((item) => {
      const img = new Image();
      img.src = item.src;
    });
  }, [items]);

  // Preload adjacent images when lightbox is open
  useEffect(() => {
    if (selectedIndex === null) return;

    const preloadImage = (src) => {
      const img = new Image();
      img.src = src;
    };

    // Preload next and previous images
    const nextIndex = (selectedIndex + 1) % clickableItems.length;
    const prevIndex = selectedIndex === 0 ? clickableItems.length - 1 : selectedIndex - 1;
    
    preloadImage(clickableItems[nextIndex].src);
    preloadImage(clickableItems[prevIndex].src);
  }, [selectedIndex, clickableItems]);

  // Handle keyboard navigation
  useEffect(() => {
    if (selectedIndex === null) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setSelectedIndex(null);
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        setSelectedIndex((prev) => {
          if (prev === null) return null;
          return prev === 0 ? clickableItems.length - 1 : prev - 1;
        });
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        setSelectedIndex((prev) => {
          if (prev === null) return null;
          return (prev + 1) % clickableItems.length;
        });
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedIndex, clickableItems.length]);

  const handleNext = () => {
    setSelectedIndex((prev) => {
      if (prev === null) return null;
      return (prev + 1) % clickableItems.length;
    });
  };

  const handlePrevious = () => {
    setSelectedIndex((prev) => {
      if (prev === null) return null;
      return prev === 0 ? clickableItems.length - 1 : prev - 1;
    });
  };

  const handleThumbnailClick = (it) => {
    const index = clickableItems.findIndex((item) => item.id === it.id);
    if (index !== -1) {
      setSelectedIndex(index);
    }
  };

  const currentImage = selectedIndex !== null ? clickableItems[selectedIndex] : null;

  return (
    <>
      <section className="gallery">
        {items.map((it, index) => {
          const isPriority = index < 5;
          const isClickable = it.clickable !== false; // default true unless explicitly false
          return (
            <div
              key={it.id}
              data-id={it.id}
              className={`item ${!isClickable ? "unclickable" : ""}`}
              style={{
                gridColumn: `${it.col} / span ${it.colSpan}`,
                gridRow: it.row ? `${it.row} / span ${it.rowSpan}` : "auto",
                zIndex: it.z || 1,
              }}
              // Only attach handler when clickable
              onClick={isClickable ? () => handleThumbnailClick(it) : undefined}
              // Accessibility niceties
              role={isClickable ? "button" : undefined}
              tabIndex={isClickable ? 0 : -1}
              aria-disabled={!isClickable || undefined}
            >
              <img
                src={it.src}
                alt={it.alt || ""}
                loading={isPriority ? "eager" : "lazy"}
                decoding="async"
                fetchpriority={isPriority ? "high" : "auto"}
                onLoad={() => setLoadedImages((prev) => new Set(prev).add(it.src))}
              />
            </div>
          );
        })}
      </section>

      {currentImage && (
        <div className="lightbox" onClick={() => setSelectedIndex(null)}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button
              className="lightbox-nav lightbox-prev"
              onClick={handlePrevious}
              aria-label="Previous image"
            >
              ‹
            </button>
            <div className="lightbox-image-wrapper">
              {!loadedImages.has(currentImage.src) && (
                <div className="lightbox-loading">Loading...</div>
              )}
              <img 
                src={currentImage.src} 
                alt={currentImage.alt || "Full view"}
                loading="eager"
                fetchpriority="high"
                onLoad={() => {
                  setLoadedImages((prev) => {
                    const next = new Set(prev);
                    next.add(currentImage.src);
                    return next;
                  });
                }}
                style={{ opacity: loadedImages.has(currentImage.src) ? 1 : 0 }}
              />
            </div>
            <button
              className="lightbox-nav lightbox-next"
              onClick={handleNext}
              aria-label="Next image"
            >
              ›
            </button>
            <button
              className="lightbox-close"
              onClick={() => setSelectedIndex(null)}
              aria-label="Close lightbox"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default GalleryGrid;
